import {
  InferenceStateFilter,
  MediaAssetQueryType,
  MediaSortKey,
  ObservationTypeFilter,
  ReviewStatusFilter,
} from "../common/QueryTypes";
import { MediaQueryCounts } from "../common/ObjectTypes";

/**
 * UI-facing filter state for the review-queue builder. Mirrors the API filter
 * dimensions but uses arrays that may be empty (empty = "no filter on this
 * dimension") and nullable scalars, which is more convenient for controlled
 * inputs. `buildMediaQuery` translates this into the wire shape.
 */
export type ReviewFilterState = {
  deploymentIds: number[];
  locationIds: number[];
  sensorIds: number[];
  captureTimeFrom: string | null;
  captureTimeTo: string | null;
  reviewStatus: ReviewStatusFilter | null;
  predictedTaxonIds: number[];
  confidenceMin: number | null;
  confidenceMax: number | null;
  observationType: ObservationTypeFilter | null;
  inferenceState: InferenceStateFilter | null;
};

export type ReviewSortState = {
  key: MediaSortKey;
  seed: number | null;
};

export type QueryValidation = {
  valid: boolean;
  /** Field-keyed validation messages for inline display. */
  errors: { confidence?: string };
};

export const emptyFilterState = (): ReviewFilterState => ({
  deploymentIds: [],
  locationIds: [],
  sensorIds: [],
  captureTimeFrom: null,
  captureTimeTo: null,
  reviewStatus: null,
  predictedTaxonIds: [],
  confidenceMin: null,
  confidenceMax: null,
  observationType: null,
  inferenceState: null,
});

/**
 * Convert a `yyyy-mm-dd` date string to an inclusive ISO 8601 boundary with
 * no time zone. Capture times are stored as the camera's wall-clock time with
 * no zone, so the picked day must reach the API unshifted by the viewer's
 * browser time zone (AKL-1006); the API passes zone-less values through as-is.
 * `endOfDay=false` yields start-of-day; `endOfDay=true` yields end-of-day so a
 * "to" bound includes the whole day.
 * @param date the `yyyy-mm-dd` string, or null
 * @param endOfDay whether to snap to the end of the day
 * @returns a zone-less ISO string, or undefined when date is null/empty/invalid
 */
export const dateToIso = (
  date: string | null,
  endOfDay: boolean,
): string | undefined => {
  if (!date) return undefined;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!match) return undefined;
  const [year, month, day] = match.slice(1).map(Number);
  // setUTCFullYear, not Date.UTC: Date.UTC maps years 0–99 to 1900–1999.
  const parsed = new Date(0);
  parsed.setUTCFullYear(year, month - 1, day);
  if (
    parsed.getUTCFullYear() !== year ||
    parsed.getUTCMonth() !== month - 1 ||
    parsed.getUTCDate() !== day
  )
    return undefined;
  return `${date}${endOfDay ? "T23:59:59.999" : "T00:00:00.000"}`;
};

/**
 * Validate the confidence bounds against the API's accepted range (0–1, min ≤
 * max). Returns a field-keyed result so the UI can show an inline message and
 * `buildMediaQuery` can refuse to emit an invalid request.
 * @param state the current filter state
 * @returns the validation result
 */
export const validateFilters = (state: ReviewFilterState): QueryValidation => {
  const { confidenceMin, confidenceMax } = state;
  const outOfRange = (v: number | null) => v !== null && (v < 0 || v > 1);
  if (outOfRange(confidenceMin) || outOfRange(confidenceMax)) {
    return {
      valid: false,
      errors: { confidence: "Confidence must be between 0 and 1." },
    };
  }
  if (
    confidenceMin !== null &&
    confidenceMax !== null &&
    confidenceMin > confidenceMax
  ) {
    return {
      valid: false,
      errors: {
        confidence: "Minimum confidence cannot exceed maximum confidence.",
      },
    };
  }
  return { valid: true, errors: {} };
};

/**
 * Build the wire-shape media query from UI state, applying the client-side
 * rules that mirror the API's 422 validation: empty list filters are omitted
 * (never sent as `[]`), `seed` is sent only for random sort, and null scalars
 * are dropped. Assumes the caller has already checked `validateFilters`.
 * @param projectId the selected project id
 * @param state the current filter state
 * @param sort the current sort state
 * @param skip offset
 * @param limit page size (null = server default)
 * @returns the request body for POST /media-assets/query
 */
export const buildMediaQuery = (
  projectId: number,
  state: ReviewFilterState,
  sort: ReviewSortState,
  skip: number,
  limit: number | null,
): MediaAssetQueryType => {
  const filters: MediaAssetQueryType["filters"] = {};

  if (state.deploymentIds.length > 0)
    filters.deployment_ids = state.deploymentIds;
  if (state.locationIds.length > 0) filters.location_ids = state.locationIds;
  if (state.sensorIds.length > 0) filters.sensor_ids = state.sensorIds;
  const from = dateToIso(state.captureTimeFrom, false);
  const to = dateToIso(state.captureTimeTo, true);
  if (from) filters.capture_time_from = from;
  if (to) filters.capture_time_to = to;
  if (state.reviewStatus) filters.review_status = state.reviewStatus;
  if (state.predictedTaxonIds.length > 0)
    filters.predicted_taxon_ids = state.predictedTaxonIds;
  if (state.confidenceMin !== null)
    filters.confidence_min = state.confidenceMin;
  if (state.confidenceMax !== null)
    filters.confidence_max = state.confidenceMax;
  if (state.observationType) filters.observation_type = state.observationType;
  if (state.inferenceState) filters.inference_state = state.inferenceState;

  return {
    project_id: projectId,
    filters,
    // seed is included only for random sort; omitted otherwise so it doesn't
    // serialize (matches the API contract: seed is only valid when key=random).
    sort:
      sort.key === "random"
        ? { key: sort.key, seed: sort.seed }
        : { key: sort.key },
    skip,
    limit,
  };
};

/**
 * Serialize the parts of a query that define its result set (filters + sort key
 * + seed) into a stable string, excluding pagination. Used to detect when the
 * page cache must be cleared: same key ⇒ cached pages stay valid.
 * @param query the media query
 * @returns a stable identity string
 */
export const queryIdentityKey = (query: MediaAssetQueryType): string =>
  JSON.stringify({
    project_id: query.project_id,
    filters: query.filters,
    sort: query.sort,
  });

export type ReviewKpis = {
  totalAssets: number;
  aiProcessed: number;
  queued: number;
  /** Share of `totalAssets` the AI has processed, 0–100. */
  processedPct: number;
  awaiting: number;
  /** Share of `totalAssets` awaiting review, 0–100 (AKL-993). */
  awaitingPct: number;
  complete: number;
  /** Share of `totalAssets` whose review is complete, 0–100 (AKL-993). */
  completePct: number;
};

/**
 * Derive the results-page KPI tile figures from the server counts payload.
 * "Processed" reads the API's `ai_processed` (AKL-755); "queued" is its
 * complement, `total - ai_processed`. All three percentages are a share of
 * `totalAssets`, every photo the current filters select, and none can exceed
 * 100.
 *
 * The review percentages used to be a share of processed media, but "awaiting"
 * counts every photo nobody has reviewed, whether or not a model has scored it.
 * On a project that was mostly still queued that read "AWAITING REVIEW 6,564 —
 * 10761% of processed" (dev) and "286 — 358% of processed" (prod). Margaux's
 * call (AKL-993, 30 Sep 2026): both review percentages are a share of all
 * photos in the filtered set, and the tiles say so. Only the percentage
 * changed; the counts are as they were.
 *
 * `ai_processed` must come from the API rather than be summed here, and that is
 * the whole point of AKL-755. This used to compute `completed + failed`, but
 * both of those states are defeated by a run in progress, so the AI Processed
 * tile read 0 (0%) for photos that had demonstrably been scored, for as long as
 * any run was going on their deployment — and, because it was then also the
 * review percentages' denominator, it took the Awaiting Review and Review
 * Complete percentages down with it. A photo being rescored is `in_progress`
 * for the filter and still processed for this tile, so no sum of the four
 * mutually-exclusive buckets can produce the figure.
 *
 * All four figures are filter-scoped as of AKL-680 styling-fixes: `totalAssets`
 * reads `counts.total` and `ai_processed` is computed with every filter
 * applied. The Total Assets tile is now the only place that number appears —
 * the queue header's "N assets match filters" caption said the same thing and
 * was removed rather than left to disagree with it.
 *
 * `by_review_status` is the exception: the API computes that facet with the
 * review-status filter EXCLUDED (self-faceted), so the filter dialog can offer
 * "awaiting review (187) / complete (16)" while one of them is selected. Read
 * straight into the tiles under a Review Status filter it described the
 * photos the filter had just removed — "TOTAL 16 · AWAITING REVIEW 187 —
 * 1169% of processed" on QA (AKL-680 triage, QA Swarm finding DATA-001). So
 * when `reviewStatus` is set, the review figures come from `total`: every
 * filtered photo is, by definition, in that one state, so its tile reads 100%
 * of all matching photos and the other reads 0%. (Over the old processed-only
 * denominator that filter could still exceed 100%: "awaiting review" on a
 * mostly-queued project divided the whole filtered total by the few processed
 * photos.)
 * @param counts the server-computed counts, or null before the first fetch
 * @param reviewStatus the Review Status filter that was in effect when
 *   `counts` was fetched, or null when none was applied
 * @returns the KPI figures, or null when counts are unavailable
 */
export const deriveKpis = (
  counts: MediaQueryCounts | null,
  reviewStatus: ReviewStatusFilter | null = null,
): ReviewKpis | null => {
  // Defensive: bail out cleanly if the KPI sub-objects aren't present yet.
  if (!counts || !counts.by_inference_state || !counts.by_review_status)
    return null;
  const totalAssets = counts.total;
  // Fall back to the pre-AKL-755 bucket sum only if the field is absent, which
  // means the web is deployed ahead of the API. Rendering the old (wrong-during-
  // a-run) figure beats rendering NaN or a flat 0, and it self-heals the moment
  // the API ships.
  const inf = counts.by_inference_state;
  const reportedProcessed =
    typeof counts.ai_processed === "number"
      ? counts.ai_processed
      : inf.completed + inf.failed;
  // The API guarantees ai_processed <= total. Clamp the figure itself rather
  // than each thing derived from it, so a violated guarantee stays consistent
  // across the whole row: queued cannot go negative AND processedPct cannot
  // read over 100%. Clamping only `queued` left the percentage free to exceed
  // 100 while the count beside it said 0 (Copilot, PR #747).
  const aiProcessed = Math.min(reportedProcessed, totalAssets);
  const queued = totalAssets - aiProcessed;
  const awaiting =
    reviewStatus === null
      ? counts.by_review_status.unreviewed
      : reviewStatus === "unreviewed"
        ? totalAssets
        : 0;
  const complete =
    reviewStatus === null
      ? counts.by_review_status.reviewed
      : reviewStatus === "reviewed"
        ? totalAssets
        : 0;
  // With no Review Status filter the API computes `by_review_status` over the
  // same filter set as `total`, so neither review count can exceed it. Capping
  // the numerator keeps "never over 100%" true even if that guarantee is ever
  // broken; the count itself is shown as sent, so the anomaly stays visible.
  const pct = (n: number, d: number) =>
    d > 0 ? Math.round((Math.min(n, d) / d) * 100) : 0;
  return {
    totalAssets,
    aiProcessed,
    queued,
    processedPct: pct(aiProcessed, totalAssets),
    awaiting,
    awaitingPct: pct(awaiting, totalAssets),
    complete,
    completePct: pct(complete, totalAssets),
  };
};
